import React, { useMemo, useState } from 'react';
import {
  CalendarDays,
  CircleDollarSign,
  CreditCard,
  GraduationCap,
  ReceiptText,
  Users,
} from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useParentData } from '../../hooks/useParentData';
import DataTable from '../ui/DataTable';
import EmptyState from '../ui/EmptyState';
import LoadingSpinner from '../ui/LoadingSpinner';
import MetricCard from '../ui/MetricCard';
import PageHeader from '../ui/PageHeader';
import StatusBadge from '../ui/StatusBadge';
import Surface from '../ui/Surface';

const paymentStatusLabel: Record<string, string> = {
  pending: 'معلق',
  paid: 'مدفوع',
  overdue: 'متأخر',
  cancelled: 'ملغي',
  refunded: 'مسترد',
};

const paymentStatusTone = (status: string) => {
  if (status === 'paid') return 'success';
  if (status === 'pending') return 'warning';
  if (status === 'overdue') return 'danger';
  return 'neutral';
};

const ParentReports: React.FC = () => {
  const { formatAmount } = useCurrency();
  const { stats, children, payments, upcomingSessions, loading } =
    useParentData();
  const [selectedChild, setSelectedChild] = useState<string>('all');

  const filteredChildren = useMemo(
    () =>
      selectedChild === 'all'
        ? children
        : children.filter(child => child.id === selectedChild),
    [children, selectedChild]
  );

  const filteredPayments = useMemo(
    () =>
      selectedChild === 'all'
        ? payments
        : payments.filter(payment => payment.studentId === selectedChild),
    [payments, selectedChild]
  );

  if (loading) {
    return (
      <Surface className='flex min-h-[24rem] items-center justify-center'>
        <LoadingSpinner size='large' />
      </Surface>
    );
  }

  const averageGrade =
    filteredChildren.length > 0
      ? Math.round(
          filteredChildren.reduce(
            (sum, child) => sum + (child.averageGrade || 0),
            0
          ) / filteredChildren.length
        )
      : 0;
  const averageAttendance =
    filteredChildren.length > 0
      ? Math.round(
          filteredChildren.reduce(
            (sum, child) => sum + (child.attendanceRate || 0),
            0
          ) / filteredChildren.length
        )
      : 0;
  const paidTotal = filteredPayments
    .filter(payment => payment.status === 'paid')
    .reduce((sum, payment) => sum + (payment.amount || 0), 0);

  return (
    <div className='space-y-6'>
      <PageHeader
        eyebrow='تقارير ولي الأمر'
        title='تقارير الأبناء'
        description='ملخص أكاديمي ومالي مبني على بيانات الأبناء والمدفوعات المتاحة من مساحات المدرسين.'
        actions={
          <select
            value={selectedChild}
            onChange={event => setSelectedChild(event.target.value)}
            className='min-h-10 rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white'
            aria-label='اختيار الابن'
          >
            <option value='all'>جميع الأبناء</option>
            {children.map(child => (
              <option key={child.id} value={child.id}>
                {child.name}
              </option>
            ))}
          </select>
        }
      />

      <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'>
        <MetricCard
          icon={Users}
          label='الأبناء في التقرير'
          value={filteredChildren.length}
          note={`${stats?.activeChildren || 0} نشط إجمالًا`}
          tone='blue'
        />
        <MetricCard
          icon={GraduationCap}
          label='متوسط الدرجات'
          value={`${averageGrade}%`}
          tone='green'
        />
        <MetricCard
          icon={CalendarDays}
          label='متوسط الحضور'
          value={`${averageAttendance}%`}
          tone='amber'
        />
        <MetricCard
          icon={CircleDollarSign}
          label='مدفوع هذا النطاق'
          value={formatAmount(paidTotal)}
          tone='green'
        />
      </div>

      <Surface padding='lg'>
        <div className='mb-4'>
          <h2 className='text-lg font-bold text-secondary-950 dark:text-white'>
            الأداء الأكاديمي
          </h2>
          <p className='mt-1 text-sm text-secondary-500 dark:text-secondary-400'>
            يعرض فقط الدرجات والحضور المسجلين فعليًا، دون توصيات ثابتة.
          </p>
        </div>
        <DataTable
          caption='أداء الأبناء'
          columns={[
            {
              key: 'child',
              header: 'الابن',
              cell: child => (
                <div>
                  <p className='font-semibold text-secondary-950 dark:text-white'>
                    {child.name}
                  </p>
                  <p className='text-xs text-secondary-500 dark:text-secondary-400'>
                    {child.grade || 'صف غير محدد'}
                  </p>
                </div>
              ),
            },
            {
              key: 'grade',
              header: 'المعدل',
              className: 'text-left tabular-nums',
              cell: child => `${child.averageGrade || 0}%`,
            },
            {
              key: 'attendance',
              header: 'الحضور',
              hideOnMobile: true,
              className: 'text-left tabular-nums',
              cell: child => `${child.attendanceRate || 0}%`,
            },
            {
              key: 'sessions',
              header: 'حصص قادمة',
              hideOnMobile: true,
              className: 'text-left tabular-nums',
              cell: child => child.upcomingSessions || 0,
            },
          ]}
          rows={filteredChildren}
          rowKey={child => child.id}
          emptyState={
            <EmptyState
              icon={Users}
              title='لا توجد بيانات أبناء'
              description='ستظهر التقارير بعد اعتماد ربط الأبناء بالمدرسين.'
              compact
            />
          }
        />
      </Surface>

      <div className='grid grid-cols-1 gap-6 xl:grid-cols-2'>
        <Surface padding='lg'>
          <div className='mb-4 flex items-center gap-3'>
            <ReceiptText className='h-5 w-5 text-primary-600' />
            <div>
              <h2 className='text-lg font-bold text-secondary-950 dark:text-white'>
                المدفوعات
              </h2>
              <p className='text-sm text-secondary-500 dark:text-secondary-400'>
                دفتر مدفوعات المدرسين المرتبطين بالأبناء.
              </p>
            </div>
          </div>
          <DataTable
            caption='مدفوعات الأبناء'
            columns={[
              {
                key: 'description',
                header: 'الوصف',
                cell: payment => payment.description || 'دفعة طالب',
              },
              {
                key: 'status',
                header: 'الحالة',
                cell: payment => (
                  <StatusBadge tone={paymentStatusTone(payment.status) as any}>
                    {paymentStatusLabel[payment.status] || payment.status}
                  </StatusBadge>
                ),
              },
              {
                key: 'amount',
                header: 'المبلغ',
                className: 'text-left tabular-nums',
                cell: payment => formatAmount(payment.amount || 0),
              },
            ]}
            rows={filteredPayments}
            rowKey={payment => payment.id}
            emptyState={
              <EmptyState
                icon={CreditCard}
                title='لا توجد مدفوعات'
                description='لا توجد مدفوعات مسجلة للنطاق الحالي.'
                compact
              />
            }
          />
        </Surface>

        <Surface padding='lg'>
          <div className='mb-4 flex items-center gap-3'>
            <CalendarDays className='h-5 w-5 text-primary-600' />
            <div>
              <h2 className='text-lg font-bold text-secondary-950 dark:text-white'>
                الحصص القادمة
              </h2>
              <p className='text-sm text-secondary-500 dark:text-secondary-400'>
                الحصص القادمة للأبناء حسب الجلسات المنشورة من المدرسين.
              </p>
            </div>
          </div>
          <DataTable
            caption='الحصص القادمة للأبناء'
            columns={[
              {
                key: 'child',
                header: 'الابن',
                cell: session => session.childName,
              },
              {
                key: 'subject',
                header: 'المادة',
                cell: session => session.subject || 'حصة دراسية',
              },
              {
                key: 'date',
                header: 'الموعد',
                className: 'text-left',
                cell: session =>
                  `${session.date.toLocaleDateString('ar-EG')} · ${
                    session.time
                  }`,
              },
            ]}
            rows={
              selectedChild === 'all'
                ? upcomingSessions
                : upcomingSessions.filter(
                    session => session.childId === selectedChild
                  )
            }
            rowKey={session => session.id}
            emptyState={
              <EmptyState
                icon={CalendarDays}
                title='لا توجد حصص قادمة'
                description='لا توجد جلسات قادمة في النطاق الحالي.'
                compact
              />
            }
          />
        </Surface>
      </div>
    </div>
  );
};

export default ParentReports;
